import type { QueryClient } from "@tanstack/react-query";
import { toast } from "@playbookai/ui";
import { PlaybookApiError } from "../api/playbook-be-client.js";

/**
 * Surface the friendly "editing is locked" toast when a canvas content write is
 * rejected because the canvas or its phase is locked (ADR-0019 `CONTENT_LOCKED`),
 * and reconcile the solve snapshot so the badge / phase-strip glyph catch up to a
 * lock applied live (softens the PB-313 live-sync gap).
 *
 * Shared by every REST content-mutation path that a lock-bound Collaborator can
 * still trigger (widget content PATCH, widget/placement delete) so none of them
 * fails silently. Keyed on the `CONTENT_LOCKED` wire code — a privilege /
 * membership 403 must NOT get this copy.
 *
 * Returns `true` if the error was a content-lock (handled here), so callers can
 * fall through to their own logging only for other errors.
 */
export function notifyContentLock(err: unknown, qc: QueryClient): boolean {
  if (
    err instanceof PlaybookApiError &&
    err.envelope.code === "CONTENT_LOCKED"
  ) {
    toast("Ask an Owner or Facilitator to unlock it before editing.", {
      title: "Editing is locked",
      tone: "watch",
    });
    void qc.invalidateQueries({ queryKey: ["solve"] });
    return true;
  }
  return false;
}

/**
 * Surface EVERY rejected canvas/solve write so none fails silently (CLAUDE.md).
 * A `CONTENT_LOCKED` rejection gets the friendly lock treatment above; anything
 * else (500, network, validation, a non-creator 404, a privilege 403) gets a
 * generic critical toast and a `console.warn` for the log — the user always
 * learns their change didn't save. Use at REST mutation error sites that were
 * previously swallowing failures (widget move/add/content-edit, solve delete).
 *
 * `title` names the failed action ("Move failed", "Couldn't delete").
 */
export function notifyWriteError(
  err: unknown,
  qc: QueryClient,
  title: string,
): void {
  // qa-004: a version conflict means a peer saved first. The caller already
  // refetched server truth (reconcile), so this is a gentle "reloaded" notice,
  // not a critical failure — distinct wire code from CONTENT_LOCKED.
  if (
    err instanceof PlaybookApiError &&
    err.envelope.code === "WIDGET_VERSION_CONFLICT"
  ) {
    toast("Reloaded with the latest — re-apply your change if needed.", {
      title: "Someone else updated this",
      tone: "info",
    });
    return;
  }
  if (
    err instanceof PlaybookApiError &&
    err.envelope.code === "WIDGET_SINGLETON_CONFLICT"
  ) {
    toast("This widget already exists on the canvas.", {
      title: "Widget already added",
      tone: "info",
    });
    return;
  }
  if (notifyContentLock(err, qc)) return;
  const detail =
    err instanceof PlaybookApiError
      ? err.envelope.message
      : err instanceof Error
        ? err.message
        : "Please try again.";
  toast(detail, { title, tone: "critical" });
  console.warn(`[solve] ${title}:`, detail);
}
